import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_TAGLINES = [
  'Your next deal, one tap away',
  'Property shopping, reinvented',
];

const pickTagline = (list) => {
  if (!Array.isArray(list) || list.length === 0) return FALLBACK_TAGLINES[0];
  return list[Math.floor(Math.random() * list.length)];
};

/**
 * SplashScreen — full-screen brand intro that fades the PropertyShop logo in,
 * holds briefly, then fades the whole overlay out before unmounting.
 * Shows a randomly-picked tagline fetched from the backend on each launch.
 *
 * Gated by sessionStorage so it only plays on fresh app loads (new tab,
 * installed PWA cold start, hard refresh) — never on in-app navigation.
 */
export const SplashScreen = () => {
  const [stage, setStage] = useState('enter'); // 'enter' | 'hold' | 'leave' | 'done'
  const [tagline, setTagline] = useState(() => pickTagline(FALLBACK_TAGLINES));

  // Fetch taglines on mount (fire-and-forget; splash uses fallback instantly).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/splash/taglines`, { timeout: 1500 })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.taglines || [])
          .map((t) => (typeof t === 'string' ? t : t?.text))
          .filter(Boolean);
        if (items.length > 0) setTagline(pickTagline(items));
      })
      .catch(() => {
        /* keep fallback */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setStage('hold'), 500);
    const t2 = setTimeout(() => setStage('leave'), 1400);
    const t3 = setTimeout(() => setStage('done'), 1900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  if (stage === 'done') return null;

  const overlayStyle = {
    opacity: stage === 'leave' ? 0 : 1,
    transition: 'opacity 500ms ease-out',
  };

  const logoStyle = {
    opacity: stage === 'enter' ? 0 : 1,
    transform: `scale(${stage === 'enter' ? 0.85 : 1})`,
    transition: 'opacity 650ms ease-out, transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const captionStyle = {
    opacity: stage === 'enter' ? 0 : 1,
    transform: `translateY(${stage === 'enter' ? '8px' : '0'})`,
    transition: 'opacity 600ms ease-out 150ms, transform 600ms ease-out 150ms',
  };

  const taglineStyle = {
    opacity: stage === 'enter' ? 0 : 1,
    transform: `translateY(${stage === 'enter' ? '10px' : '0'})`,
    transition: 'opacity 700ms ease-out 350ms, transform 700ms ease-out 350ms',
  };

  const barStyle = {
    width: stage === 'enter' ? '0%' : stage === 'hold' || stage === 'leave' ? '100%' : '0%',
    transition: 'width 1000ms cubic-bezier(0.65, 0, 0.35, 1) 200ms',
  };

  return (
    <div
      data-testid="splash-screen"
      aria-hidden="true"
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      style={{
        ...overlayStyle,
        background:
          'radial-gradient(circle at 50% 40%, #2D3742 0%, #212A31 55%, #15191F 100%)',
        pointerEvents: stage === 'leave' ? 'none' : 'auto',
      }}
    >
      <div className="flex flex-col items-center px-6 text-center max-w-md">
        <img
          src="/logo.png"
          alt="PropertyShop"
          width={140}
          height={140}
          draggable={false}
          style={logoStyle}
          className="w-[140px] h-[140px] rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] select-none"
        />
        <div
          style={captionStyle}
          className="mt-7 text-3xl tracking-wide"
          data-testid="splash-wordmark"
        >
          <span className="font-serif font-semibold text-white/95">Property</span>
          <span className="font-serif font-semibold text-[#F97316]">Shop</span>
        </div>
        <p
          style={captionStyle}
          className="mt-2 text-[11px] uppercase tracking-[0.35em] text-white/40"
        >
          Real Estate, Reinvented
        </p>
        <p
          style={taglineStyle}
          data-testid="splash-tagline"
          className="mt-5 text-sm text-white/70 italic"
        >
          {tagline}
        </p>
        <div className="mt-8 h-[2px] w-40 overflow-hidden rounded-full bg-white/10">
          <div style={barStyle} className="h-full bg-[#F97316]" />
        </div>
      </div>
    </div>
  );
};

/**
 * Gate wrapper — renders the splash only once per browser session.
 */
export const SplashGate = () => {
  const [show, setShow] = useState(() => {
    try {
      return typeof window !== 'undefined' && !sessionStorage.getItem('ps_splash_shown');
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!show) return;
    try {
      sessionStorage.setItem('ps_splash_shown', '1');
    } catch {
      /* ignore */
    }
    const t = setTimeout(() => setShow(false), 2000);
    return () => clearTimeout(t);
  }, [show]);

  return show ? <SplashScreen /> : null;
};
